import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMaintenance } from '../context/MaintenanceContext';
import { Wrench, Eye, Power, FloppyDisk, Warning } from '@phosphor-icons/react';

/** MaintenancePanel — admin UI to control the live banner / takeover.
 *
 *  - Mode dropdown (off / banner / takeover)
 *  - Title + message inputs (with character counters)
 *  - Severity radio (info / warning / critical) — drives banner colour
 *  - Expires-at picker (datetime-local; auto-converts to ISO UTC). Blank
 *    means no auto-dismiss.
 *  - Bypass admins checkbox (default ON — so we don't lock ourselves out).
 *  - Live preview of the rendered banner.
 *  - Save button + "Turn off" emergency kill switch.
 */
export default function MaintenancePanel() {
    const { cfg, refresh, setConfig } = useMaintenance();
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!draft && cfg) {
            setDraft({
                mode: cfg.mode || 'off',
                title: cfg.title || '',
                message: cfg.message || '',
                severity: cfg.severity || 'info',
                expires_at_local: isoToLocalInput(cfg.expires_at),
                bypass_admins: cfg.bypass_admins !== false,
            });
        }
    }, [cfg, draft]);

    const dirty = useMemo(() => {
        if (!draft || !cfg) return false;
        return (
            draft.mode !== (cfg.mode || 'off') ||
            draft.title !== (cfg.title || '') ||
            draft.message !== (cfg.message || '') ||
            draft.severity !== (cfg.severity || 'info') ||
            draft.expires_at_local !== isoToLocalInput(cfg.expires_at) ||
            draft.bypass_admins !== (cfg.bypass_admins !== false)
        );
    }, [draft, cfg]);

    if (!draft) return <PanelSkeleton />;

    const save = async () => {
        if (draft.mode !== 'off' && !draft.message.trim() && !draft.title.trim()) {
            toast.error('Add a title or message before going live.');
            return;
        }
        setSaving(true);
        try {
            await setConfig({
                mode: draft.mode,
                title: draft.title.trim(),
                message: draft.message.trim(),
                severity: draft.severity,
                expires_at: localInputToIso(draft.expires_at_local),
                bypass_admins: draft.bypass_admins,
            });
            toast.success(draft.mode === 'off'
                ? 'Maintenance turned off.'
                : `${draft.mode === 'takeover' ? 'Takeover' : 'Banner'} is now live.`);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Could not save.';
            toast.error(typeof detail === 'string' ? detail : 'Could not save.');
        } finally {
            setSaving(false);
        }
    };

    const turnOff = async () => {
        if (!window.confirm('Turn off the live banner / takeover for all users?')) return;
        setSaving(true);
        try {
            await setConfig({
                mode: 'off',
                title: draft.title,
                message: draft.message,
                severity: draft.severity,
                expires_at: localInputToIso(draft.expires_at_local),
                bypass_admins: draft.bypass_admins,
            });
            setDraft(d => ({ ...d, mode: 'off' }));
            toast.success('Maintenance turned off.');
            await refresh();
        } catch {
            toast.error('Could not turn off.');
        } finally {
            setSaving(false);
        }
    };

    const isLive = cfg?.mode && cfg.mode !== 'off';
    const liveSince = cfg?.updated_at ? new Date(cfg.updated_at).toLocaleString('en-GB') : '';

    return (
        <section className="border border-[#18181B]" data-testid="admin-maintenance-panel">
            <div className="px-4 py-3 border-b border-[#18181B] flex items-center justify-between">
                <h2 className="font-display font-semibold inline-flex items-center gap-2">
                    <Wrench size={18} weight="duotone" /> Live banner / maintenance mode
                </h2>
                {isLive ? (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[#7F1D1D] text-white px-2 py-1" data-testid="maintenance-live-pill">
                        {cfg.mode} live
                    </span>
                ) : (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[#E4E4E7] text-[#52525B] px-2 py-1" data-testid="maintenance-off-pill">
                        Off
                    </span>
                )}
            </div>

            <div className="p-4 space-y-4">
                {/* Mode */}
                <div>
                    <label className="label-tiny mb-1.5 block">Mode</label>
                    <div className="flex flex-wrap items-center gap-2" data-testid="maintenance-mode-group">
                        {[
                            { id: 'off',      label: 'Off' },
                            { id: 'banner',   label: 'Banner only' },
                            { id: 'takeover', label: 'Full takeover' },
                        ].map(opt => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setDraft(d => ({ ...d, mode: opt.id }))}
                                data-testid={`maintenance-mode-${opt.id}`}
                                className={`px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border ${draft.mode === opt.id ? 'bg-[#18181B] text-white border-[#18181B]' : 'border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Title + message */}
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="label-tiny mb-1.5 block">Title</label>
                        <input
                            value={draft.title}
                            onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))}
                            placeholder="Scheduled maintenance"
                            maxLength={120}
                            data-testid="maintenance-title-input"
                            className="w-full border border-[#18181B] px-3 py-2 text-sm"
                        />
                        <div className="text-[10px] text-[#A1A1AA] mt-0.5 text-right">{draft.title.length} / 120</div>
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Severity</label>
                        <div className="flex items-center gap-2">
                            {[
                                { id: 'info',     label: 'Info',     tone: 'bg-[#0F4C5C] text-white' },
                                { id: 'warning',  label: 'Warning',  tone: 'bg-[#B45309] text-white' },
                                { id: 'critical', label: 'Critical', tone: 'bg-[#7F1D1D] text-white' },
                            ].map(opt => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setDraft(d => ({ ...d, severity: opt.id }))}
                                    data-testid={`maintenance-severity-${opt.id}`}
                                    className={`px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border ${draft.severity === opt.id ? `${opt.tone} border-transparent` : 'border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="label-tiny mb-1.5 block">Message</label>
                    <textarea
                        value={draft.message}
                        onChange={(e) => setDraft(d => ({ ...d, message: e.target.value }))}
                        placeholder="We're upgrading the AI snag analyser and will be back in about 30 minutes."
                        maxLength={600}
                        rows={3}
                        data-testid="maintenance-message-input"
                        className="w-full border border-[#18181B] px-3 py-2 text-sm leading-relaxed resize-y"
                    />
                    <div className="text-[10px] text-[#A1A1AA] mt-0.5 text-right">{draft.message.length} / 600</div>
                </div>

                {/* Auto-dismiss + admin bypass */}
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="label-tiny mb-1.5 block">Auto-dismiss at (optional)</label>
                        <input
                            type="datetime-local"
                            value={draft.expires_at_local}
                            onChange={(e) => setDraft(d => ({ ...d, expires_at_local: e.target.value }))}
                            data-testid="maintenance-expires-input"
                            className="w-full border border-[#18181B] px-3 py-2 text-sm font-mono"
                        />
                        <p className="text-[10px] text-[#71717A] mt-1">Blank = stays live until you toggle it off. Local browser time, stored as UTC.</p>
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Admin bypass</label>
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={draft.bypass_admins}
                                onChange={(e) => setDraft(d => ({ ...d, bypass_admins: e.target.checked }))}
                                data-testid="maintenance-bypass-input"
                                className="h-4 w-4"
                            />
                            <span>Admins can still use the app (recommended)</span>
                        </label>
                        {!draft.bypass_admins && draft.mode === 'takeover' ? (
                            <p className="text-[11px] text-[#7F1D1D] mt-1.5 flex items-center gap-1">
                                <Warning size={12} weight="fill" /> You will be locked out too — only `/api/maintenance` PUT (via curl) can reverse this.
                            </p>
                        ) : null}
                    </div>
                </div>

                {/* Live preview */}
                {draft.mode !== 'off' && (draft.title || draft.message) ? (
                    <div data-testid="maintenance-preview">
                        <label className="label-tiny mb-1.5 inline-flex items-center gap-1.5"><Eye size={12} weight="bold" /> Preview</label>
                        {draft.mode === 'banner' ? (
                            <div className={`w-full px-3 py-2 flex items-center gap-2 text-white ${draft.severity === 'critical' ? 'bg-[#7F1D1D]' : draft.severity === 'warning' ? 'bg-[#B45309]' : 'bg-[#0F4C5C]'}`}>
                                <Warning size={16} weight="fill" />
                                <div className="text-sm">
                                    {draft.title ? <span className="font-display font-semibold mr-2">{draft.title}</span> : null}
                                    <span className="text-white/90">{draft.message || '(message)'}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-[#09090B] text-white p-6 text-center">
                                <div className={`inline-flex items-center gap-2 px-3 py-1 mb-3 font-mono text-[10px] uppercase tracking-[0.2em] ${draft.severity === 'critical' ? 'bg-[#7F1D1D]' : draft.severity === 'warning' ? 'bg-[#B45309]' : 'bg-[#0F4C5C]'}`}>
                                    <Wrench size={12} weight="bold" /> Maintenance in progress
                                </div>
                                <div className="font-display text-xl font-bold">{draft.title || "We'll be right back"}</div>
                                {draft.message ? <div className="mt-2 text-sm text-white/80 whitespace-pre-line">{draft.message}</div> : null}
                            </div>
                        )}
                    </div>
                ) : null}

                {/* Save row */}
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E4E4E7]">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !dirty}
                        data-testid="maintenance-save-btn"
                        className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-white ${saving || !dirty ? 'bg-[#1FA5CF]/40 cursor-not-allowed' : 'bg-[#1FA5CF] hover:bg-[#1787AB] active:scale-95'} transition-transform`}
                    >
                        <FloppyDisk size={14} weight="bold" /> {saving ? 'Saving…' : dirty ? 'Save & publish' : 'Saved'}
                    </button>
                    {isLive ? (
                        <button
                            type="button"
                            onClick={turnOff}
                            disabled={saving}
                            data-testid="maintenance-turn-off-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] border border-[#7F1D1D] text-[#7F1D1D] hover:bg-[#FEE2E2] active:scale-95 transition-transform"
                        >
                            <Power size={14} weight="bold" /> Turn off now
                        </button>
                    ) : null}
                    {cfg?.updated_by ? (
                        <span className="ml-auto text-[11px] text-[#71717A]">
                            Last updated by {cfg.updated_by} · {liveSince}
                        </span>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

function PanelSkeleton() {
    return (
        <section className="border border-[#18181B]">
            <div className="px-4 py-3 border-b border-[#18181B] font-display font-semibold inline-flex items-center gap-2">
                <Wrench size={18} weight="duotone" /> Live banner / maintenance mode
            </div>
            <div className="p-8 text-center text-[#52525B] text-sm">Loading current config…</div>
        </section>
    );
}

/** Convert a stored ISO UTC string into the `datetime-local` input value
 *  in the user's local timezone. Empty string if no value. */
function isoToLocalInput(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
}

/** Convert a `datetime-local` value (local TZ) into ISO UTC string. */
function localInputToIso(local) {
    if (!local) return null;
    try {
        const d = new Date(local);
        if (isNaN(d.getTime())) return null;
        return d.toISOString();
    } catch { return null; }
}
